<template>
  <div
    class="min-h-screen w-full flex flex-col bg-gradient-to-br from-spicy-mustard-300 to-mocha-mousse-300"
  >
    <!-- Header Bar -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col items-center justify-center px-6 py-10">
      <!-- Logo -->
      <!-- <a
        :href="$router.resolve('/').href"
        class="group inline-flex items-center justify-center"
      >
        <img
          class="h-32 w-auto drop-shadow-sm transition-transform group-hover:scale-105"
          :src="logo"
          alt="Enodo — ties manuscripts to their data"
        />
      </a> -->

      <!-- Actions -->
      <div class="mt-10 flex gap-4 w-full max-w-5xl flex-col sm:flex-row">
        <!-- Browse Data -->
        <component
          :is="!shouldEnforceAuthForBrowse ? 'router-link' : 'div'"
          :to="!shouldEnforceAuthForBrowse ? '/show' : null"
          @click="shouldEnforceAuthForBrowse && handleBrowseDataClick()"
          class="flex-1 min-h-[180px] flex flex-col items-center justify-center gap-4 p-6 rounded-xl shadow-sm transition-all duration-200 ease-out group relative"
          :class="
            !shouldEnforceAuthForBrowse
              ? 'bg-white hover:shadow-lg hover:-translate-y-1 hover:border-gray-200 border-2 border-transparent border-t-amber-500'
              : 'bg-white/70 opacity-70 border-2 border-transparent border-t-gray-400 cursor-pointer'
          "
        >
          <div class="relative">
            <Icon
              icon="mdi:folder-eye-outline"
              class="w-10 h-10 text-spicy-mustard-600 group-hover:scale-110 transition-transform duration-200"
            />
            <Icon
              v-if="shouldEnforceAuthForBrowse"
              icon="mdi:shield-lock-outline"
              class="w-5 h-5 absolute -top-1 -right-2 text-amber-500"
            />
          </div>
          <span
            class="text-2xl font-semibold text-center"
            :class="
              !shouldEnforceAuthForBrowse ? 'text-gray-800' : 'text-gray-500'
            "
            >Browse Data</span
          >
          <span v-if="shouldEnforceAuthForBrowse" class="text-sm text-gray-400"
            >Sign in required</span
          >
        </component>

        <!-- My Enodo Data -->
        <component
          :is="!shouldEnforceAuthForMyUploads ? 'router-link' : 'div'"
          :to="!shouldEnforceAuthForMyUploads ? '/myUploads' : null"
          @click="shouldEnforceAuthForMyUploads && handleMyUploadsClick()"
          class="flex-1 min-h-[180px] flex flex-col items-center justify-center gap-4 p-6 rounded-xl shadow-sm transition-all duration-200 ease-out group relative"
          :class="
            !shouldEnforceAuthForMyUploads
              ? 'bg-white hover:shadow-lg hover:-translate-y-1 hover:border-gray-200 border-2 border-transparent border-t-teal-500'
              : 'bg-white/70 opacity-70 border-2 border-transparent border-t-gray-400 cursor-pointer'
          "
        >
          <div class="relative">
            <Icon
              icon="mdi:folder-account-outline"
              class="w-10 h-10 group-hover:scale-110 transition-transform duration-200"
              :class="
                !shouldEnforceAuthForMyUploads
                  ? 'text-teal-600'
                  : 'text-gray-400'
              "
            />
            <Icon
              v-if="shouldEnforceAuthForMyUploads"
              icon="mdi:shield-lock-outline"
              class="w-5 h-5 absolute -top-1 -right-2 text-teal-500"
            />
          </div>
          <span
            class="text-2xl font-semibold text-center"
            :class="
              !shouldEnforceAuthForMyUploads ? 'text-gray-800' : 'text-gray-500'
            "
            >My Uploads</span
          >
          <span
            v-if="shouldEnforceAuthForMyUploads"
            class="text-sm text-gray-400"
            >Sign in required</span
          >
        </component>

        <!-- Upload Data -->
        <component
          :is="!shouldEnforceAuthForUpload ? 'router-link' : 'div'"
          :to="!shouldEnforceAuthForUpload ? '/form/new' : null"
          @click="shouldEnforceAuthForUpload && handleUploadClick()"
          class="flex-1 min-h-[180px] flex flex-col items-center justify-center gap-4 p-6 rounded-xl shadow-sm transition-all duration-200 ease-out group relative"
          :class="
            !shouldEnforceAuthForUpload
              ? 'bg-white hover:shadow-lg hover:-translate-y-1 hover:border-gray-200 border-2 border-transparent border-t-periwinkle-500'
              : 'bg-white/70 opacity-70 border-2 border-transparent border-t-gray-400 cursor-pointer'
          "
        >
          <div class="relative">
            <Icon
              icon="mdi:database-plus-outline"
              class="w-10 h-10 transition-colors duration-200 group-hover:scale-110 transition-transform duration-200"
              :class="
                !shouldEnforceAuthForUpload
                  ? 'text-periwinkle-500'
                  : 'text-gray-400'
              "
            />
            <Icon
              v-if="shouldEnforceAuthForUpload"
              icon="mdi:shield-lock-outline"
              class="w-5 h-5 absolute -top-1 -right-2 text-amber-500"
            />
          </div>
          <span
            class="text-2xl font-semibold text-center"
            :class="
              !shouldEnforceAuthForUpload ? 'text-gray-800' : 'text-gray-500'
            "
            >Upload Data</span
          >
          <span v-if="shouldEnforceAuthForUpload" class="text-sm text-gray-400"
            >Sign in required</span
          >
        </component>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";
import authService, {
  currentUser,
  currentUserIsAdmin,
} from "@/services/authService";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";

const router = useRouter();
const logo = ref(null);

// Use reactive currentUser and currentUserIsAdmin from authService
const isAuthenticated = computed(() => !!currentUser.value);
const isAdmin = computed(() => currentUserIsAdmin.value);

// Check if specific routes require auth
const browseDataRequiresAuth = computed(() => {
  const route = router.resolve("/show");
  return route.meta.requiresAuth !== false;
});

const myUploadsRequiresAuth = computed(() => {
  const route = router.resolve("/myUploads");
  return route.meta.requiresAuth !== false;
});

const uploadDataRequiresAuth = computed(() => {
  const route = router.resolve("/form/new");
  return route.meta.requiresAuth !== false;
});

// Only enforce auth check if the route requires it and user is not authenticated
const shouldEnforceAuthForBrowse = computed(
  () => browseDataRequiresAuth.value && !isAuthenticated.value,
);
const shouldEnforceAuthForMyUploads = computed(
  () => myUploadsRequiresAuth.value && !isAuthenticated.value,
);
const shouldEnforceAuthForUpload = computed(
  () => uploadDataRequiresAuth.value && !isAuthenticated.value,
);

onMounted(async () => {
  const base = import.meta?.env?.BASE_URL || process.env.BASE_URL || "/";
  logo.value = `${base}images/enodo-logo.png`;

  // No need to check auth here - App.vue already handles it on mount
  // and currentUser is reactive
});

const handleUploadClick = () => {
  // Redirect to login page with return URL
  router.push({ path: "/login", query: { redirect: "/form/new" } });
};

const handleMyUploadsClick = () => {
  // Redirect to login page with return URL
  router.push({ path: "/login", query: { redirect: "/myUploads" } });
};

const handleBrowseDataClick = () => {
  // Redirect to login page with return URL
  router.push({ path: "/login", query: { redirect: "/show" } });
};
</script>
